import { useSite } from "@inrange/building-manager-api-client";
import {
  AggregateEnergyFlow,
  AllowStringForNumbers,
  BatterySpec,
  EditMode,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { BorderedCheckbox } from "@inrange/theme-components";
import { currencySymbol } from "@inrange/theme-components/formatting";
import { Icons } from "@inrange/theme-components/icons";
import { sortBy } from "@inrange/utils";
import { Col, Form, Row } from "react-bootstrap";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import BatteryTooltip from "./BatteryTooltip";
import {
  currencyValue,
  formatNumber,
  formatNumber2dp,
  parseAsNumber,
} from "./utils";

const BatterySettings = ({
  site,
  esys,
  setSite,
  currencyCode,
  editMode,
}: {
  site: SiteAllowStringValues;
  esys: AllowStringForNumbers<SiteElectricalSystem>;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  currencyCode: string;
  editMode: EditMode;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);

  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });

  const knownBatteries: Record<string, BatterySpec> = fetchSiteValues.data
    ? fetchSiteValues.data.options?.batteries[currencyCode]
    : {};

  const batteryModelName = site.battery[esys.batteryIdx].batterySpec?.modelName;
  const batteryCostKey = site.battery[esys.batteryIdx].batterySpec
    ? (batteryModelName ?? "Custom")
    : undefined;

  const isKnownModel = !!(
    batteryModelName && !!knownBatteries[batteryModelName]
  );

  const energyFlowAnnual = siteCalculations?.esys?.[esys.idx]?.energyFlowAnnual;
  const energyFlowMonthly =
    siteCalculations?.esys?.[esys.idx]?.energyFlowMonthly;

  return (
    <div id="battery-settings">
      <Row className="d-flex flex-wrap align-items-end">
        <Col sm={3}>
          <Form.Group
            className="mb-3"
            controlId={`batteryModel-${esys.batteryIdx}`}
          >
            <Form.Label>
              Model
              {!!site.battery[esys.batteryIdx].batterySpec && (
                <span
                  onClick={() => {
                    setSite({
                      battery: {
                        [esys.batteryIdx]: {
                          batteryEnergyFlowPayloadHash:
                            null as unknown as undefined,
                          batteryEnergyFlowFileVersionId:
                            null as unknown as undefined,
                          batterySpec: null as unknown as undefined,
                        },
                      },
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                  data-testid="remove-battery"
                >
                  <img
                    alt="remove"
                    src={Icons.trashcanRed}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginLeft: "6px",
                      cursor: "pointer",
                    }}
                  ></img>
                </span>
              )}
            </Form.Label>
            <Form.Select
              data-testid="batteryModelSelect"
              value={site.battery[esys.batteryIdx].batterySpec?.modelName ?? ""}
              onChange={(e) => {
                const batteryModelName = e.target.value;
                if (batteryModelName === "") {
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          capacityMwh: "",
                          powerMw: "",
                          modelName: null,
                          count: 1,
                        },
                      },
                    },
                    costInputsBattery: {
                      costs: {
                        [`Custom`]: {
                          hardwareCost: 0,
                          installationCost: 0,
                        },
                      },
                    },
                  });
                } else {
                  const batterySpec = knownBatteries[batteryModelName];
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          ...batterySpec,
                          count:
                            site.battery[esys.batteryIdx].batterySpec?.count ??
                            1,
                        },
                      },
                    },
                    costInputsBattery: {
                      costs: {
                        [batteryModelName]: batterySpec.defaultCost,
                      },
                    },
                    costInputsInrange: {
                      batteryCosts: {
                        [batteryModelName]: batterySpec.defaultMfrCost
                          ? batterySpec.defaultMfrCost
                          : { hardwareCost: "", installationCost: "" },
                      },
                    },
                  });
                }
              }}
            >
              <option value="">No set model</option>
              {Object.entries(knownBatteries)
                .filter(
                  ([name, spec]) =>
                    !spec.deprecated ||
                    name ===
                      site.battery[esys.batteryIdx].batterySpec?.modelName
                )
                .sort(sortBy(([_, spec]) => spec.modelName))
                .map(([batteryModelName, _], index) => (
                  <option key={index}>{batteryModelName}</option>
                ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group
            className="mb-3"
            controlId={`numBatteries-${esys.batteryIdx}`}
          >
            <Form.Label>Num batteries</Form.Label>
            <Form.Control
              type="text"
              value={
                isKnownModel
                  ? site.battery[esys.batteryIdx].batterySpec?.count
                  : ""
              }
              disabled={!isKnownModel}
              onChange={(e) => {
                setSite({
                  battery: {
                    [esys.batteryIdx]: {
                      batterySpec: {
                        count: e.target.value,
                      },
                    },
                  },
                });
              }}
              isInvalid={
                !!errors[`battery.${esys.batteryIdx}.batterySpec.count`]
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors[`battery.${esys.batteryIdx}.batterySpec.count`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Power (MW)</Form.Label>
            <Form.Control
              type="text"
              value={
                site.battery[esys.batteryIdx].batterySpec
                  ? formatNumber(
                      isKnownModel
                        ? parseAsNumber(
                            site.battery[esys.batteryIdx].batterySpec.powerMw
                          ) *
                            parseAsNumber(
                              site.battery[esys.batteryIdx].batterySpec.count
                            )
                        : site.battery[esys.batteryIdx].batterySpec.powerMw,
                      0,
                      6,
                      false
                    )
                  : ""
              }
              disabled={isKnownModel}
              onChange={(e) => {
                setSite({
                  battery: {
                    [esys.batteryIdx]: {
                      batterySpec: {
                        powerMw: formatNumber(e.target.value, 1, 6, false),
                      },
                    },
                  },
                });
              }}
              isInvalid={
                !!errors[`battery.${esys.batteryIdx}.batterySpec.powerMw`]
              }
              data-testid={"batteryPower"}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`battery.${esys.batteryIdx}.batterySpec.powerMw`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>
              {site.battery[esys.batteryIdx].batterySpec && energyFlowAnnual ? (
                <BatteryTooltip
                  batterySpec={site.battery[esys.batteryIdx].batterySpec}
                  energyFlowAnnual={
                    energyFlowAnnual as unknown as AggregateEnergyFlow
                  }
                  energyFlowMonthly={
                    energyFlowMonthly as unknown as {
                      [month: number]: AggregateEnergyFlow;
                    }
                  }
                >
                  <span
                    style={{
                      textDecoration: "dotted underline 1px",
                    }}
                  >
                    Capacity (MWh)
                  </span>
                </BatteryTooltip>
              ) : (
                <>Capacity (MWh)</>
              )}
            </Form.Label>
            <Form.Control
              type="text"
              value={
                site.battery[esys.batteryIdx].batterySpec
                  ? formatNumber(
                      isKnownModel
                        ? parseAsNumber(
                            site.battery[esys.batteryIdx].batterySpec
                              .capacityMwh
                          ) *
                            parseAsNumber(
                              site.battery[esys.batteryIdx].batterySpec.count
                            )
                        : site.battery[esys.batteryIdx].batterySpec.capacityMwh,
                      0,
                      6,
                      false
                    )
                  : ""
              }
              disabled={isKnownModel}
              onChange={(e) => {
                setSite({
                  battery: {
                    [esys.batteryIdx]: {
                      batterySpec: {
                        capacityMwh: formatNumber(e.target.value, 1, 6, false),
                      },
                    },
                  },
                });
              }}
              isInvalid={
                !!errors[`battery.${esys.batteryIdx}.batterySpec.capacityMwh`]
              }
              data-testid={"batteryCapacity"}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`battery.${esys.batteryIdx}.batterySpec.capacityMwh`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {editMode === "SINGLE" && (
          <>
            <Col sm={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Per batt. HW to funder ({currencySymbol(currencyCode)})
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formatNumber(
                    (batteryCostKey
                      ? site.costInputsBattery?.costs?.[batteryCostKey]
                          ?.hardwareCost
                      : undefined) ?? "",
                    2,
                    6,
                    false
                  )}
                  onChange={(e) => {
                    setSite({
                      costInputsBattery: {
                        costs: {
                          [batteryCostKey!]: {
                            hardwareCost: formatNumber(
                              e.target.value,
                              2,
                              6,
                              false
                            ),
                          },
                        },
                      },
                    });
                  }}
                  disabled={!batteryCostKey}
                  isInvalid={
                    !!errors[
                      `costInputsBattery.costs.${batteryCostKey}.hardwareCost`
                    ]
                  }
                  data-testid={"batteryCost"}
                />
                <Form.Control.Feedback type="invalid">
                  {
                    errors[
                      `costInputsBattery.costs.${batteryCostKey}.hardwareCost`
                    ]
                  }
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group
                className="mb-3"
                controlId={`batteryInstallationCost-${esys.batteryIdx}`}
              >
                <Form.Label>Per batt. install to funder</Form.Label>
                <Form.Control
                  type="text"
                  value={formatNumber(
                    (batteryCostKey
                      ? site.costInputsBattery?.costs?.[batteryCostKey]
                          ?.installationCost
                      : undefined) ?? "",
                    2,
                    6,
                    false
                  )}
                  onChange={(e) => {
                    setSite({
                      costInputsBattery: {
                        costs: {
                          [batteryCostKey!]: {
                            installationCost: formatNumber(
                              e.target.value,
                              2,
                              6,
                              false
                            ),
                          },
                        },
                      },
                    });
                  }}
                  disabled={!batteryCostKey}
                  isInvalid={
                    !!errors[
                      `costInputsBattery.costs.${batteryCostKey}.installationCost`
                    ]
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {
                    errors[
                      `costInputsBattery.costs.${batteryCostKey}.installationCost`
                    ]
                  }
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-3">
                <Form.Label>Total HW cost to funder</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    batteryCostKey
                      ? formatNumber(
                          parseAsNumber(
                            site.costInputsBattery?.costs?.[batteryCostKey]
                              ?.hardwareCost ?? ""
                          ) *
                            parseAsNumber(
                              site.battery[esys.batteryIdx].batterySpec?.count
                            ),
                          2,
                          6,
                          false
                        )
                      : ""
                  }
                  disabled
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-3">
                <Form.Label>Total install cost to funder</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    batteryCostKey
                      ? formatNumber(
                          parseAsNumber(
                            site.costInputsBattery?.costs?.[batteryCostKey]
                              ?.installationCost ?? ""
                          ) *
                            parseAsNumber(
                              site.battery[esys.batteryIdx].batterySpec?.count
                            ),
                          2,
                          6,
                          false
                        )
                      : ""
                  }
                  disabled
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Label>Enable tariff arbitrage</Form.Label>
              <Form
                style={{
                  display: "flex",
                  fontSize: 12,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <BorderedCheckbox
                  dataTestId={`battery-arbitrage-true`}
                  formName="battery-arbitrage"
                  value={"true"}
                  label={"True"}
                  selectedOption={
                    site.tariffConfig.arbitrageEnabled ? "true" : "false"
                  }
                  setSelectedOption={(value) =>
                    setSite({
                      tariffConfig: {
                        arbitrageEnabled: value === "true",
                      },
                    })
                  }
                />
                <BorderedCheckbox
                  dataTestId={`battery-arbitrage-false`}
                  formName="battery-arbitrage"
                  value={"false"}
                  label={"False"}
                  selectedOption={
                    site.tariffConfig.arbitrageEnabled ? "true" : "false"
                  }
                  setSelectedOption={(value) =>
                    setSite({
                      tariffConfig: {
                        arbitrageEnabled: value === "true",
                      },
                    })
                  }
                />
              </Form>
            </Col>
            {site.tariffConfig.arbitrageEnabled && (
              <Col sm={3}>
                <Form.Group
                  className="mb-3"
                  controlId={`arbitrageY1-${esys.idx}`}
                >
                  <Form.Label>Peak shifting value (Y1)</Form.Label>
                  <Form.Control
                    disabled
                    value={currencyValue(
                      siteCalculations.financialModelsInrange
                        ?.tariffArbitrageRevenue.grossProcurementValue || 0,
                      siteCalculations.currencyCode
                    )}
                  />
                </Form.Group>
              </Col>
            )}
          </>
        )}
      </Row>
      <CollapsibleCard title={"Advanced"} className="mb-3" initialOpen={false}>
        <Row className="d-flex flex-wrap align-items-end">
          {editMode === "SINGLE" && (
            <>
              <Col sm={3}>
                <Form.Group
                  className="mb-3"
                  controlId={`irBatteryHardwareCost-${esys.batteryIdx}`}
                >
                  <Form.Label>Per batt. mfr. HW quote</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatNumber(
                      (batteryCostKey
                        ? site.costInputsInrange?.batteryCosts?.[batteryCostKey]
                            ?.hardwareCost
                        : undefined) ?? "",
                      2,
                      6,
                      false
                    )}
                    onChange={(e) => {
                      setSite({
                        costInputsInrange: {
                          batteryCosts: {
                            [batteryCostKey!]: {
                              hardwareCost: formatNumber(
                                e.target.value,
                                2,
                                6,
                                false
                              ),
                            },
                          },
                        },
                      });
                    }}
                    disabled={!batteryCostKey}
                    isInvalid={
                      !!errors[
                        `costInputsInrange.batteryCosts.${batteryCostKey}.hardwareCost`
                      ]
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors[
                        `costInputsInrange.batteryCosts.${batteryCostKey}.hardwareCost`
                      ]
                    }
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group
                  className="mb-3"
                  controlId={`irBatteryInstallationCost-${esys.batteryIdx}`}
                >
                  <Form.Label>Per batt. installer quote</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatNumber(
                      (batteryCostKey
                        ? site.costInputsInrange?.batteryCosts?.[batteryCostKey]
                            ?.installationCost
                        : undefined) ?? "",
                      2,
                      6,
                      false
                    )}
                    onChange={(e) => {
                      setSite({
                        costInputsInrange: {
                          batteryCosts: {
                            [batteryCostKey!]: {
                              installationCost: formatNumber(
                                e.target.value,
                                2,
                                6,
                                false
                              ),
                            },
                          },
                        },
                      });
                    }}
                    disabled={!batteryCostKey}
                    isInvalid={
                      !!errors[
                        `costInputsInrange.costs.${batteryCostKey}.installationCost`
                      ]
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors[
                        `costInputsInrange.costs.${batteryCostKey}.installationCost`
                      ]
                    }
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Total Mfr. HW quote</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      batteryCostKey &&
                      site.costInputsInrange?.batteryCosts?.[batteryCostKey]
                        ?.hardwareCost
                        ? formatNumber(
                            parseAsNumber(
                              site.costInputsInrange?.batteryCosts?.[
                                batteryCostKey
                              ]?.hardwareCost ?? ""
                            ) *
                              parseAsNumber(
                                site.battery[esys.batteryIdx].batterySpec?.count
                              ),
                            2,
                            6,
                            false
                          )
                        : ""
                    }
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Total batt. installer quote</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      batteryCostKey &&
                      site.costInputsInrange?.batteryCosts?.[batteryCostKey]
                        ?.installationCost
                        ? formatNumber(
                            parseAsNumber(
                              site.costInputsInrange?.batteryCosts?.[
                                batteryCostKey
                              ]?.installationCost ?? ""
                            ) *
                              parseAsNumber(
                                site.battery[esys.batteryIdx].batterySpec?.count
                              ),
                            2,
                            6,
                            false
                          )
                        : ""
                    }
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group
                  className="mb-3"
                  controlId={`irBatteryInstallationCost-${esys.batteryIdx}`}
                >
                  <Form.Label>
                    Cost/kWp to funder ({currencySymbol(currencyCode)}/kWp)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      batteryCostKey &&
                      parseAsNumber(site.totalInstalledCapacity) > 0
                        ? currencyValue(
                            (parseAsNumber(
                              site.costInputsBattery.costs[batteryCostKey!]
                                ?.hardwareCost
                            ) +
                              parseAsNumber(
                                site.costInputsBattery.costs[batteryCostKey!]
                                  ?.installationCost
                              )) /
                              parseAsNumber(site.totalInstalledCapacity),
                            currencyCode
                          )
                        : ""
                    }
                    disabled={true}
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group
                  className="mb-3"
                  controlId={`irBatteryInstallationCost-${esys.batteryIdx}`}
                >
                  <Form.Label>
                    Mfr. cost/kWh ({currencySymbol(currencyCode)}/kWh)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      batteryCostKey &&
                      site.battery[esys.batteryIdx].batterySpec
                        ? currencyValue(
                            (parseAsNumber(
                              site.costInputsInrange!.batteryCosts[
                                batteryCostKey
                              ]?.hardwareCost ||
                                site.costInputsBattery.costs[batteryCostKey]
                                  ?.hardwareCost
                            ) +
                              parseAsNumber(
                                site.costInputsInrange!.batteryCosts[
                                  batteryCostKey
                                ]?.installationCost ||
                                  site.costInputsBattery.costs[batteryCostKey]
                                    ?.installationCost
                              )) /
                              (parseAsNumber(
                                site.battery[esys.batteryIdx].batterySpec
                                  .capacityMwh
                              ) *
                                1000),
                            currencyCode
                          )
                        : ""
                    }
                    disabled={true}
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`batteryModelNotes-${esys.batteryIdx}`}
            >
              <Form.Label>Model notes</Form.Label>
              <Form.Control
                type="text"
                value={
                  site.battery[esys.batteryIdx].batterySpec?.modelNotes ?? ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          modelNotes: e.target.value,
                        },
                      },
                    },
                  })
                }
                isInvalid={
                  !!errors[`battery.${esys.batteryIdx}.batterySpec.modelNotes`]
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors[`battery.${esys.batteryIdx}.batterySpec.modelNotes`]}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`batteryWarrantyYears-${esys.batteryIdx}`}
            >
              <Form.Label>Replacement (years)</Form.Label>
              <Form.Control
                type="text"
                value={
                  site.battery[esys.batteryIdx].batterySpec?.warranty
                    ?.periodYears ?? ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          warranty: {
                            periodYears: e.target.value,
                          },
                        },
                      },
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `battery.${esys.batteryIdx}.batterySpec.warranty.periodYears`
                  ]
                }
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `battery.${esys.batteryIdx}.batterySpec.warranty.periodYears`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`batteryWarrantyCycles-${esys.batteryIdx}`}
            >
              <Form.Label>Replacement (cycles)</Form.Label>
              <Form.Control
                type="text"
                value={
                  site.battery[esys.batteryIdx].batterySpec?.warranty
                    ?.periodCycles ?? ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          warranty: {
                            periodCycles: e.target.value,
                          },
                        },
                      },
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `battery.${esys.batteryIdx}.batterySpec.warranty.periodCycles`
                  ]
                }
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `battery.${esys.batteryIdx}.batterySpec.warranty.periodCycles`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`batteryWarrantyCapacity-${esys.batteryIdx}`}
            >
              <Form.Label>End of warranty capacity (%)</Form.Label>
              <FormInputPercentage
                value={
                  site.battery[esys.batteryIdx].batterySpec?.warranty
                    ?.capacity ?? ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          warranty: {
                            capacity: formatNumber(e.target.value, 1, 6, false),
                          },
                        },
                      },
                    },
                  })
                }
                errorKey={`battery.${esys.batteryIdx}.batterySpec.warranty.capacity`}
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `battery.${esys.batteryIdx}.batterySpec.warranty.capacity`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group className="mb-3">
              <Form.Label>Depth of discharge (%)</Form.Label>
              <FormInputPercentage
                value={
                  site.battery[esys.batteryIdx].batterySpec?.depthOfDischarge ||
                  ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          depthOfDischarge: formatNumber(
                            e.target.value,
                            1,
                            6,
                            false
                          ),
                        },
                      },
                    },
                  })
                }
                errorKey={`battery.${esys.batteryIdx}.batterySpec.depthOfDischarge`}
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `battery.${esys.batteryIdx}.batterySpec.depthOfDischarge`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group className="mb-3">
              <Form.Label>Round trip efficiency (%)</Form.Label>
              <FormInputPercentage
                value={
                  site.battery[esys.batteryIdx].batterySpec
                    ?.roundTripEfficiency ?? ""
                }
                onChange={(e) =>
                  setSite({
                    battery: {
                      [esys.batteryIdx]: {
                        batterySpec: {
                          roundTripEfficiency: formatNumber(
                            e.target.value,
                            1,
                            6,
                            false
                          ),
                        },
                      },
                    },
                  })
                }
                errorKey={`battery.${esys.batteryIdx}.batterySpec.roundTripEfficiency`}
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `battery.${esys.batteryIdx}.batterySpec.roundTripEfficiency`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`simVersion-${esys.batteryIdx}`}
            >
              <Form.Label>Simulation version</Form.Label>
              <Form.Select
                value={site.costInputsBattery.simVersion}
                onChange={(e) =>
                  setSite({
                    costInputsBattery: {
                      simVersion: e.target.value,
                    },
                  })
                }
              >
                <option value={"v1"}>v1 (EPL)</option>
                <option value={"v2"}>v2 (CVXPY)</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`arbitrageY1-gridcharge-${esys.batteryIdx}`}
            >
              <Form.Label>Battery ch. (from grid, kWh)</Form.Label>
              <Form.Control
                disabled
                value={formatNumber2dp(
                  energyFlowAnnual?.batteryGridCharge || 0
                )}
              />
            </Form.Group>
          </Col>
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`arbitrageY1-griddischarge-${esys.batteryIdx}`}
            >
              <Form.Label>Battery dis. (grid energy, kWh)</Form.Label>
              <Form.Control
                disabled
                value={formatNumber2dp(
                  energyFlowAnnual?.batteryGridDischarge || 0
                )}
              />
            </Form.Group>
          </Col>
        </Row>
      </CollapsibleCard>
    </div>
  );
};

export default BatterySettings;
